// ---------------------------------------------------------------------------
// Language and text direction.
//
// Switching to Arabic does three things at once:
//   1. swaps the dictionary,
//   2. sets dir="rtl" on <html> so the whole layout mirrors,
//   3. sets lang="ar" so the Cairo font applies.
//
// Because the direction lives on <html>, Tailwind's logical properties
// (ms-, me-, ps-, pe-, start-, end-) flip automatically. Components should use
// those rather than left/right, so nothing has to be written twice.
// ---------------------------------------------------------------------------

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { dictionaries, type Translations } from './translations';
import type { Locale } from '../types';

const STORAGE_KEY = 'yaghi.locale';

interface I18nContextValue {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  t: Translations;
  setLocale: (locale: Locale) => void;
  toggleLocale: () => void;
  /// Replace {placeholders} in a string: format(t.login.pinFor, { name: 'Ali' })
  format: (template: string, values: Record<string, string | number>) => string;
  /// Pick the Arabic or English field of a bilingual record.
  pick: (record: { nameEn: string; nameAr: string } | null | undefined) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'ar') return stored;
  } catch {
    // localStorage can be unavailable in private browsing. Fall through.
  }
  // Arabic is the shop's day-to-day language, so it is the default.
  return 'ar';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale);

  const dir: 'ltr' | 'rtl' = locale === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    const root = document.documentElement;
    root.lang = locale;
    root.dir = dir;
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // Not being able to remember the choice is not worth an error.
    }
  }, [locale, dir]);

  const setLocale = useCallback((next: Locale) => setLocaleState(next), []);
  const toggleLocale = useCallback(() => setLocaleState((current) => (current === 'ar' ? 'en' : 'ar')), []);

  const format = useCallback((template: string, values: Record<string, string | number>) => {
    return template.replace(/\{(\w+)\}/g, (match, key: string) =>
      key in values ? String(values[key]) : match,
    );
  }, []);

  const pick = useCallback(
    (record: { nameEn: string; nameAr: string } | null | undefined) => {
      if (!record) return '';
      return locale === 'ar' ? record.nameAr : record.nameEn;
    },
    [locale],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, dir, t: dictionaries[locale], setLocale, toggleLocale, format, pick }),
    [locale, dir, setLocale, toggleLocale, format, pick],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n was used outside of I18nProvider.');
  }
  return context;
}
